import React, { useState, useEffect } from 'react';
import { FileText, List, Send, Briefcase, LogOut, LayoutDashboard, Users, Database, FileStack, Receipt, BarChart3, History, Menu, X, Wrench } from 'lucide-react';
import axios from 'axios';
import './AdminDashboard.css';

// Import the separate page components
import JDGenerator from './JDGenerator';
import GeneratedJDs from './GeneratedJDs';
import JobPosting from './JobPosting';
import PostedJobs from './PostedJobs';
import UserManagement from './UserManagement';
import AppliedDatabase from './AppliedDatabase';
import RegisteredDatabase from './RegisteredDatabase';
import BeelineJobDetails from './BeelineJobDetails';
import Invoices from './Invoices'; // <--- NEW IMPORT
import AnalyticsDashboard from './AnalyticsDashboard'; // <--- NEW IMPORT
import ResumeWatermark from './ResumeWatermark';

export default function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('job-posting');
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile sidebar toggle

  // --- DATA STORES ---
  const [savedJDs, setSavedJDs] = useState([]);
  const [editingJD, setEditingJD] = useState(null);
  
  // Data state
  const [postedJobs, setPostedJobs] = useState([]);
  
  // Track the job currently being edited
  const [editingJob, setEditingJob] = useState(null);

  // --- State for Assignable Partners (Beeline) ---
  const [assignableVendors, setAssignableVendors] = useState([]);
  const [assignableRecruiters, setAssignableRecruiters] = useState([]);

  // --- NEW: LIFTED STATE FOR BEELINE TOGGLE ---
  // Keeps track of the "Beeline" vs "Standard" toggle across views
  const [beelineMode, setBeelineMode] = useState(false);

  // --- NEW: State for Beeline Tracking ---
  const [beelineJobToTrack, setBeelineJobToTrack] = useState(null);

  // Fetch all data on component load or tab change
  useEffect(() => {
    // Optimization: Only fetch job data if we are NOT on specific management tabs
    if (activeTab !== 'user-management' && activeTab !== 'registered-db' && activeTab !== 'applied-db' && activeTab !== 'invoices' && activeTab !== 'analytics-dashboard' && activeTab !== 'analytics-history' && activeTab !== 'resume-watermark') {
       fetchJobs();
       fetchJDs();
       fetchPartners(); 
    }
  }, [activeTab]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/admin`);
      setPostedJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const fetchJDs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jds`);
      setSavedJDs(response.data);
    } catch (error) {
      console.error("Error fetching JDs:", error);
    }
  };

  // --- Fetch Vendors and Recruiters for Job Posting Dropdown ---
  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/assignable-partners`);
      if (response.data) {
        setAssignableVendors(response.data.vendors || []);
        setAssignableRecruiters(response.data.recruiters || []);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  // --- HANDLERS ---
  const handleSaveJD = async (jdData) => {
    try {
      if (jdData._id) { 
        const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/jds/${jdData._id}`, jdData);
        setSavedJDs(prev => prev.map(item => item._id === jdData._id ? response.data : item));
      } else {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/jds`, jdData);
        setSavedJDs(prev => [response.data, ...prev]);
      }
      setEditingJD(null);
    } catch (error) {
      console.error("Error saving JD:", error);
      alert("Failed to save JD to database.");
    }
  };

  const handleEditJD = (jd) => {
    setEditingJD(jd);
    setActiveTab('jd-generator');
  };

  const goToTab = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false); // auto-close drawer on mobile after navigating
  };

  const handleNewJDClick = () => {
    setEditingJD(null);
    goToTab('jd-generator');
  };

  const handlePostOrUpdateJob = async (jobData) => {
    try {
      let jobId = null;
      if (jobData instanceof FormData) {
        jobId = jobData.get('_id');
        if (jobId === 'undefined' || jobId === 'null' || jobId === '') jobId = null;
      } else {
        jobId = jobData._id;
      }

      if (jobId) {
        const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/${jobId}`, jobData);
        setPostedJobs(prev => prev.map(job => job._id === jobId ? response.data : job));
        alert("Job Updated Successfully!");
      } else {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/jobs`, jobData);
        setPostedJobs(prev => [response.data, ...prev]);
        alert("Job Posted Successfully!");
      }
      
      setEditingJob(null);
      setActiveTab('posted-jobs');

    } catch (error) {
      console.error("Error saving job:", error);
      alert("Operation failed. See console for details.");
    }
  };

  const handleEditClick = (job) => {
    setEditingJob(job); 
    setActiveTab('job-posting'); 
  };

  const handleNewPostClick = () => {
    setEditingJob(null); 
    goToTab('job-posting');
  };

  // --- Handler for clicking a Beeline Job ---
  const handleViewBeeline = (job) => {
    setBeelineJobToTrack(job);
    setActiveTab('beeline-details'); 
  };

  // --- RENDER CONTENT SWITCHER ---
  const renderContent = () => {
    switch (activeTab) {
      case 'user-management': 
        return <UserManagement />;
      
      case 'registered-db': 
        return <RegisteredDatabase />;

      case 'applied-db': 
        return <AppliedDatabase />;

      case 'jd-generator': 
        return <JDGenerator onSave={handleSaveJD} initialData={editingJD} />;
      
      case 'generated-jds': 
        return <GeneratedJDs jds={savedJDs} onEdit={handleEditJD} />;
      
      case 'job-posting': 
        return (
          <JobPosting 
            onPost={handlePostOrUpdateJob} 
            availableJDs={savedJDs} 
            initialData={editingJob}
            availableVendors={assignableVendors}
            availableRecruiters={assignableRecruiters}
          />
        );
      
      case 'posted-jobs': 
        return (
          <PostedJobs 
            jobs={postedJobs} 
            onEdit={handleEditClick} 
            onViewBeeline={handleViewBeeline} 
            // PASSING LIFTED STATE DOWN
            externalShowBeeline={beelineMode} 
            setExternalShowBeeline={setBeelineMode}
          />
        );

      case 'beeline-details': 
        return (
          <BeelineJobDetails 
            job={beelineJobToTrack} 
            onBack={() => {
                setBeelineJobToTrack(null);
                setActiveTab('posted-jobs');
                // State 'beelineMode' is preserved here in AdminDashboard
            }} 
          />
        );

      // --- NEW CASE ---
      case 'invoices':
        return <Invoices />;

      case 'analytics-dashboard':
        return <AnalyticsDashboard />;

      case 'resume-watermark':
        return <ResumeWatermark />;
      
      default: 
        return <PostedJobs jobs={postedJobs} />;
    }
  };

  return (
    <div className="dashboard-container">
      {/* Overlay closes sidebar when tapping outside it on mobile */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo-icon">
            <LayoutDashboard size={24} color="white" />
          </div>
          <h2>TalentOza</h2>
        </div>
        
        <div className="sidebar-scroll">
          <div className="sidebar-section-label">Admin Controls</div>
          <nav className="sidebar-nav">
             <button 
               className={`nav-item ${activeTab === 'user-management' ? 'active' : ''}`} 
               onClick={() => goToTab('user-management')}
             >
              <Users size={20} /><span>User Management</span>
            </button>
          </nav>

          <div className="sidebar-section-label">Finance</div>
          <nav className="sidebar-nav">
             <button 
               className={`nav-item ${activeTab === 'invoices' ? 'active' : ''}`} 
               onClick={() => goToTab('invoices')}
             >
              <Receipt size={20} /><span>Invoices</span>
            </button>
          </nav>

          <div className="sidebar-section-label">Recruitment</div>
          <nav className="sidebar-nav">
            <button className={`nav-item ${activeTab === 'jd-generator' ? 'active' : ''}`} onClick={handleNewJDClick}>
              <FileText size={20} /><span>JD Generator</span>
            </button>
            <button className={`nav-item ${activeTab === 'generated-jds' ? 'active' : ''}`} onClick={() => goToTab('generated-jds')}>
              <List size={20} /><span>Generated JDs</span>
            </button>
            <button className={`nav-item ${activeTab === 'job-posting' ? 'active' : ''}`} onClick={handleNewPostClick}>
              <Send size={20} /><span>Job Posting</span>
            </button>
            <button className={`nav-item ${activeTab === 'posted-jobs' ? 'active' : ''}`} onClick={() => goToTab('posted-jobs')}>
              <Briefcase size={20} /><span>Posted Jobs</span>
            </button>
          </nav>
          
          <div className="sidebar-section-label">Master Databases</div>
          <nav className="sidebar-nav">
             <button className={`nav-item ${activeTab === 'registered-db' ? 'active' : ''}`} onClick={() => goToTab('registered-db')}>
              <Database size={20} /><span>Registered Candidates</span>
            </button>
             <button className={`nav-item ${activeTab === 'applied-db' ? 'active' : ''}`} onClick={() => goToTab('applied-db')}>
              <FileStack size={20} /><span>All Applicants</span>
            </button>
          </nav>

          <div className="sidebar-section-label">Tools</div>
          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeTab === 'resume-watermark' ? 'active' : ''}`}
              onClick={() => goToTab('resume-watermark')}
            >
              <Wrench size={20} /><span>Resume Watermark</span>
            </button>
          </nav>

          <div className="sidebar-section-label">Analytics</div>
          <nav className="sidebar-nav">
             <button className={`nav-item ${activeTab === 'analytics-dashboard' ? 'active' : ''}`} onClick={() => goToTab('analytics-dashboard')}>
              <BarChart3 size={20} /><span>Dashboard</span>
            </button>
          </nav>
        </div>

        <div className="sidebar-footer">
          <button className="nav-item logout-btn" onClick={onLogout}>
            <LogOut size={20} /><span>Log Out</span>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="top-bar">
          <div className="top-bar-left">
            <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)}>
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <h2 className="top-bar-title">{activeTab.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</h2>
          </div>
          <div className="user-profile-pill"><div className="user-avatar">A</div><span className="user-name-text">Admin User</span></div>
        </div>
        <div className="content-wrapper">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
